import axios from 'axios';

import type {
    ApiExternalUserProfile,
    ApiFavoriteProduct,
    ApiUserProfile,
    ApiUserProfilePatchRequest,
    CoopUserProfileApiRecipeRatingResponse,
} from './userProfileModels';

import directProfileApiAxiosClient from '../clients/directProfileApiAxiosClient';
import getLocalApiAxiosClient from '../clients/localApiAxiosClient';

export const userProfileDirectApiUrls = {
    getProfile: '/v1/profile',
    createDefaultProfile: '/v1/profile',
    updateProfile: '/v1/profile',
    getAllFavoriteProducts: '/v1/profile/favorite-products',
    addFavoriteProduct: '/v1/profile/favorite-products',
    removeFavoriteProduct: '/v1/profile/favorite-products',
    getCurrentUserProfile: '/userprofile/currentUser/',
    updateCoopRatingCache: '/api/userprofile/clearratingcache/',
} satisfies {
    [k in keyof typeof userProfileDirectApi]: `/${string}`;
};

const updateCoopRatingCache = async ({
    recipeId,
    rating1to5,
}: {
    recipeId: number;
    rating1to5: number;
}): Promise<CoopUserProfileApiRecipeRatingResponse> => {
    const response = await axios.put(userProfileDirectApiUrls.updateCoopRatingCache, {
        recipeId,
        rating: rating1to5,
    });

    return response.data;
};

const getCurrentUserProfile = async (): Promise<ApiUserProfile> => {
    const response = await getLocalApiAxiosClient().get(
        userProfileDirectApiUrls.getCurrentUserProfile,
    );
    return response.data;
};

const getProfile = async (accessToken: string) => {
    const response = await directProfileApiAxiosClient(accessToken).get<ApiExternalUserProfile>(
        userProfileDirectApiUrls.getProfile,
        {
            // dont throw on 404 and 20x
            validateStatus: (status) => (status >= 200 && status < 300) || status === 404,
        },
    );

    if (response.status === 404) {
        return null;
    }

    return response.data;
};

const createDefaultProfile = async (accessToken: string) => {
    const response =
        await directProfileApiAxiosClient(accessToken).post<ApiExternalUserProfile>('v1/profile');
    return response.data;
};

const updateProfile = async (accessToken: string, patchRequest: ApiUserProfilePatchRequest) => {
    const response = await directProfileApiAxiosClient(accessToken).patch<ApiExternalUserProfile>(
        userProfileDirectApiUrls.updateProfile,
        patchRequest,
    );
    return response.data;
};

const getAllFavoriteProducts = async (accessToken: string) => {
    const response = await directProfileApiAxiosClient(accessToken).get<ApiFavoriteProduct[]>(
        userProfileDirectApiUrls.getAllFavoriteProducts,
    );
    return response.data;
};

const addFavoriteProduct = async (accessToken: string, productId: string) => {
    const response = await directProfileApiAxiosClient(accessToken).patch<ApiFavoriteProduct>(
        userProfileDirectApiUrls.addFavoriteProduct,
        { productId },
    );
    return response.data;
};

const removeFavoriteProduct = async (accessToken: string, productId: string) => {
    const response = await directProfileApiAxiosClient(accessToken).delete(
        `${userProfileDirectApiUrls.removeFavoriteProduct}/${productId}`,
    );
    return response.data;
};

export const userProfileDirectApi = {
    updateCoopRatingCache,
    getCurrentUserProfile,
    getProfile,
    createDefaultProfile,
    updateProfile,
    getAllFavoriteProducts,
    addFavoriteProduct,
    removeFavoriteProduct,
};
